import React, { useState, useMemo } from 'react';
import { Calculator, Users, Clock, TrendingUp, Building } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Average hourly rates per category (min, max)
const CATEGORY_RATES: Record<string, [number, number]> = {
    admin: [8, 15],
    support: [12, 25],
    marketing: [20, 40],
    design: [25, 50],
    development: [30, 60],
    legal: [40, 80],
    finance: [35, 70],
    executive: [50, 100],
};

const PLAN_CONFIG = {
    team_starter: { fee: 199, commission: 0.20, label: 'Team Starter' },
    business: { fee: 499, commission: 0.15, label: 'Business' },
    enterprise_custom: { fee: 999, commission: 0.10, label: 'Enterprise' },
};

export default function CostCalculator() {
    const { t } = useTranslation('legal');

    const [selectedPlan, setSelectedPlan] = useState<keyof typeof PLAN_CONFIG>('business');
    const [selectedCategory, setSelectedCategory] = useState('development');
    const [numReplicas, setNumReplicas] = useState(3);
    const [hoursPerMonth, setHoursPerMonth] = useState(80);

    const calculations = useMemo(() => {
        const plan = PLAN_CONFIG[selectedPlan];
        const [min, max] = CATEGORY_RATES[selectedCategory];
        const avgRate = (min + max) / 2;

        const platformFee = plan.fee;
        const grossReplicaCost = avgRate * numReplicas * hoursPerMonth;
        const commission = grossReplicaCost * plan.commission;
        const totalCost = platformFee + grossReplicaCost + commission;
        const creatorsEarn = grossReplicaCost;

        return {
            platformFee,
            grossReplicaCost,
            commission,
            totalCost,
            creatorsEarn,
            avgRate,
            commissionPct: plan.commission * 100,
        };
    }, [selectedPlan, selectedCategory, numReplicas, hoursPerMonth]);

    const formatCurrency = (val: number) => {
        return `€${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const categories = Object.keys(CATEGORY_RATES);

    return (
        <div className="mt-20 max-w-4xl mx-auto" id="cost-calculator">
            {/* Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hig-accent/10 text-hig-accent text-sm font-medium mb-4">
                    <Calculator size={16} />
                    {t('pricing_page.calculator.title')}
                </div>
                <h2 className="text-3xl font-bold text-hig-text-primary mb-3">
                    {t('pricing_page.calculator.title')}
                </h2>
                <p className="text-hig-text-secondary max-w-2xl mx-auto">
                    {t('pricing_page.calculator.subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Controls */}
                <div className="lg:col-span-3 card p-6 space-y-6">
                    {/* Plan Selection */}
                    <div>
                        <label className="block text-sm font-medium text-hig-text-primary mb-2">
                            {t('pricing_page.calculator.select_plan')}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {(Object.keys(PLAN_CONFIG) as Array<keyof typeof PLAN_CONFIG>).map((planKey) => (
                                <button
                                    key={planKey}
                                    onClick={() => setSelectedPlan(planKey)}
                                    className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${selectedPlan === planKey
                                            ? 'bg-hig-accent text-white shadow-lg shadow-hig-accent/25'
                                            : 'bg-hig-bg-tertiary text-hig-text-secondary hover:bg-hig-bg-elevated hover:text-hig-text-primary'
                                        }`}
                                >
                                    {PLAN_CONFIG[planKey].label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Category Selection */}
                    <div>
                        <label className="block text-sm font-medium text-hig-text-primary mb-2">
                            {t('pricing_page.calculator.replica_category')}
                        </label>
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full bg-hig-bg-tertiary text-hig-text-primary border border-hig-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-hig-accent/50"
                        >
                            {categories.map((cat) => {
                                const [min, max] = CATEGORY_RATES[cat];
                                return (
                                    <option key={cat} value={cat}>
                                        {t(`pricing_page.calculator.categories.${cat}`)} (€{min}–€{max}/hr)
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Number of Replicas */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-hig-text-primary flex items-center gap-2">
                                <Users size={14} className="text-hig-accent" />
                                {t('pricing_page.calculator.num_replicas')}
                            </label>
                            <span className="text-sm font-semibold text-hig-accent bg-hig-accent/10 px-2.5 py-0.5 rounded-full">
                                {numReplicas} {t('pricing_page.calculator.replicas')}
                            </span>
                        </div>
                        <input
                            type="range"
                            min={1}
                            max={20}
                            value={numReplicas}
                            onChange={(e) => setNumReplicas(Number(e.target.value))}
                            className="w-full h-2 bg-hig-bg-tertiary rounded-lg appearance-none cursor-pointer accent-hig-accent"
                        />
                        <div className="flex justify-between text-xs text-hig-text-tertiary mt-1">
                            <span>1</span>
                            <span>20</span>
                        </div>
                    </div>

                    {/* Hours per Month */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-hig-text-primary flex items-center gap-2">
                                <Clock size={14} className="text-hig-accent" />
                                {t('pricing_page.calculator.hours_per_month')}
                            </label>
                            <span className="text-sm font-semibold text-hig-accent bg-hig-accent/10 px-2.5 py-0.5 rounded-full">
                                {hoursPerMonth} {t('pricing_page.calculator.hours')}
                            </span>
                        </div>
                        <input
                            type="range"
                            min={10}
                            max={160}
                            step={10}
                            value={hoursPerMonth}
                            onChange={(e) => setHoursPerMonth(Number(e.target.value))}
                            className="w-full h-2 bg-hig-bg-tertiary rounded-lg appearance-none cursor-pointer accent-hig-accent"
                        />
                        <div className="flex justify-between text-xs text-hig-text-tertiary mt-1">
                            <span>10 hrs</span>
                            <span>160 hrs (full-time)</span>
                        </div>
                    </div>
                </div>

                {/* Results */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Cost Breakdown Card */}
                    <div className="card p-6 bg-gradient-to-br from-hig-bg-secondary to-hig-bg-tertiary border border-hig-border">
                        <h3 className="text-sm font-semibold text-hig-text-tertiary uppercase tracking-wider mb-4">
                            Monthly Estimate
                        </h3>

                        <div className="space-y-3">
                            {/* Platform Fee */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Building size={14} className="text-hig-text-tertiary" />
                                    <span className="text-sm text-hig-text-secondary">{t('pricing_page.calculator.platform_fee')}</span>
                                </div>
                                <span className="text-sm font-medium text-hig-text-primary">{formatCurrency(calculations.platformFee)}</span>
                            </div>

                            {/* Replica Costs */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Users size={14} className="text-hig-text-tertiary" />
                                    <span className="text-sm text-hig-text-secondary">{t('pricing_page.calculator.replica_costs')}</span>
                                </div>
                                <span className="text-sm font-medium text-hig-text-primary">{formatCurrency(calculations.grossReplicaCost)}</span>
                            </div>

                            {/* Commission */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <TrendingUp size={14} className="text-hig-text-tertiary" />
                                    <span className="text-sm text-hig-text-secondary">
                                        {t('pricing_page.calculator.commission')} ({calculations.commissionPct}%)
                                    </span>
                                </div>
                                <span className="text-sm font-medium text-hig-text-primary">{formatCurrency(calculations.commission)}</span>
                            </div>

                            <div className="border-t border-hig-divider my-2" />

                            {/* Total */}
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-hig-text-primary">{t('pricing_page.calculator.total_cost')}</span>
                                <span className="text-xl font-bold text-hig-accent">{formatCurrency(calculations.totalCost)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Creators Earn Card */}
                    <div className="card p-4 border border-emerald-500/20 bg-emerald-500/5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-emerald-400 font-medium uppercase tracking-wider">{t('pricing_page.calculator.creators_earn')}</p>
                                <p className="text-lg font-bold text-emerald-400">{formatCurrency(calculations.creatorsEarn)}<span className="text-sm font-normal text-emerald-400/70">{t('pricing_page.calculator.per_month')}</span></p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                                <TrendingUp size={20} className="text-emerald-400" />
                            </div>
                        </div>
                        <p className="text-xs text-hig-text-tertiary mt-2">
                            Based on avg. {formatCurrency(calculations.avgRate)}/hr × {numReplicas} replicas × {hoursPerMonth} hrs
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
